/**
 * Build a KPI with Cognara — plain-English → saved project KPI widget.
 * Mirrors the Structural Workfront KPI builder, scoped to project modules.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Sparkles, Loader2, Send, Save, Trash2, Plus, ChevronLeft, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { usePanelTheme } from './panelTheme';

const SEEDS = [
  'Average engineering progress by discipline',
  'Procurement items not started, by discipline',
  'Construction man-hours by discipline',
];

const WidgetChart = ({ w, T }) => {
  const rows = w.rows || [];
  const palette = [T.accent, T.good, T.violet, T.warn, T.teal, T.bad, T.muted];
  if (w.spec?.chartType === 'stat') {
    return (
      <div className="py-5 text-center">
        <div className="text-3xl font-bold font-mono" style={{ color: T.accent }}>
          {w.headline}{w.unit}
        </div>
        <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: T.muted }}>{w.spec?.title}</div>
      </div>
    );
  }
  if (w.spec?.chartType === 'table') {
    return (
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2 text-[11px] py-1"
               style={{ borderBottom: `1px solid ${T.border}` }}>
            <span className="flex-1 truncate" style={{ color: T.text2 }}>{r.label}</span>
            <span className="font-mono font-semibold" style={{ color: T.text }}>{r.value}{w.unit}</span>
          </div>
        ))}
      </div>
    );
  }
  if (w.spec?.chartType === 'donut') {
    return (
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="label" innerRadius={48} outerRadius={78} paddingAngle={2}>
              {rows.map((r, i) => <Cell key={r.label} fill={palette[i % palette.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.text }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }
  return (
    <div style={{ height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false} />
          <XAxis dataKey="label" tick={{ fill: T.muted, fontSize: 9 }} interval={0} angle={-22} textAnchor="end" height={44} />
          <YAxis tick={{ fill: T.muted, fontSize: 9 }} />
          <Tooltip contentStyle={{ background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.text }} />
          <Bar dataKey="value" radius={[3, 3, 0, 0]}>
            {rows.map((r, i) => <Cell key={r.label} fill={palette[i % palette.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const KpiWidgetEditor = ({ projectId, widget, T, onSaved, onCancel }) => {
  const [spec, setSpec] = useState({ ...widget.spec });
  const [options, setOptions] = useState(null);
  const [preview, setPreview] = useState({ rows: widget.rows, unit: widget.unit, headline: widget.headline, spec });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/v1/projects/${projectId}/overview/kpi-builder/options?module=${spec.module}`)
      .then((r) => setOptions(r.data))
      .catch(() => setOptions(null));
  }, [projectId, spec.module]);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      api.post(`/v1/projects/${projectId}/overview/kpi-builder/preview`, { spec })
        .then((r) => { if (alive) setPreview(r.data); })
        .catch(() => {})
        .finally(() => { if (alive) setBusy(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [projectId, spec]);

  const set = (k) => (v) => setSpec((s) => ({ ...s, [k]: v, ...(k === 'module' ? { filterGroup: '', filterOwner: '' } : {}) }));

  const Field = ({ label, children }) => (
    <label className="block">
      <span className="text-[9px] uppercase tracking-wider block mb-0.5" style={{ color: T.muted }}>{label}</span>
      {children}
    </label>
  );
  const Sel = ({ value, onChange, children, testId }) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full text-[10px] rounded-lg px-2 py-1.5 outline-none"
      style={{ background: T.inner, border: `1px solid ${T.border}`, color: T.text }}>{children}</select>
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/v1/projects/${projectId}/overview/kpi-builder/widgets/${widget.widgetId}`, { spec });
      toast.success('KPI updated');
      onSaved();
    } catch { toast.error('Could not update that KPI'); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl p-3 space-y-2" data-testid={`kpi-widget-editor-${widget.widgetId}`}
         style={{ background: T.inner, border: `1px solid ${T.violet}66` }}>
      <div className="flex items-center gap-2">
        <Pencil className="w-3 h-3" style={{ color: T.violet }} />
        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: T.violet }}>
          Editing KPI
        </span>
        {busy && <Loader2 className="w-3 h-3 animate-spin ml-auto" style={{ color: T.muted }} />}
      </div>

      <Field label="Title">
        <input value={spec.title || ''} onChange={(e) => set('title')(e.target.value)}
          className="w-full text-[11px] rounded-lg px-2 py-1.5 outline-none"
          style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text }}
          data-testid="kpi-edit-title" />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Module">
          <Sel value={spec.module} onChange={set('module')} testId="kpi-edit-module">
            {(options?.modules || [{ key: spec.module, label: spec.module }]).map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </Sel>
        </Field>
        <Field label="Metric">
          <Sel value={spec.metric} onChange={set('metric')} testId="kpi-edit-metric">
            {(options?.metrics || [{ key: spec.metric, label: spec.metric }]).map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </Sel>
        </Field>
        <Field label="Chart type">
          <Sel value={spec.chartType} onChange={set('chartType')} testId="kpi-edit-chart">
            {(options?.charts || ['bar']).map((c) => <option key={c} value={c}>{c}</option>)}
          </Sel>
        </Field>
        <Field label="Rows">
          <Sel value={String(spec.limit || 8)} onChange={(v) => set('limit')(Number(v))} testId="kpi-edit-limit">
            {[3, 5, 8, 10, 12, 15, 20].map((n) => <option key={n} value={String(n)}>{n}</option>)}
          </Sel>
        </Field>
        <Field label={options?.groupLabel || 'Discipline'}>
          <Sel value={spec.filterGroup} onChange={set('filterGroup')} testId="kpi-edit-group">
            <option value="">All</option>
            {(options?.groups || []).map((g) => <option key={g} value={g}>{g}</option>)}
          </Sel>
        </Field>
        <Field label={options?.ownerLabel || 'Contractor'}>
          <Sel value={spec.filterOwner} onChange={set('filterOwner')} testId="kpi-edit-owner">
            <option value="">All</option>
            {(options?.owners || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </Sel>
        </Field>
      </div>

      <WidgetChart w={preview} T={T} />

      <div className="flex items-center gap-2">
        <button type="button" onClick={save} disabled={saving}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold disabled:opacity-50"
          style={{ background: T.violet, color: '#fff' }} data-testid="kpi-edit-save">
          <Save className="w-3.5 h-3.5" /> Save changes
        </button>
        <button type="button" onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-[11px] font-semibold"
          style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text2 }}
          data-testid="kpi-edit-cancel">Cancel</button>
      </div>
    </div>
  );
};

export const ProjectKpiBuilder = ({ projectId, asPanel = false }) => {
  const T = usePanelTheme();
  const [widgets, setWidgets] = useState([]);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [draft, setDraft] = useState(null);
  const [ask, setAsk] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const endRef = useRef(null);

  const loadWidgets = useCallback(() => {
    if (!projectId) return;
    api.get(`/v1/projects/${projectId}/overview/kpi-builder/widgets`)
      .then((r) => setWidgets(r.data?.widgets || []))
      .catch(() => setWidgets([]));
  }, [projectId]);

  useEffect(() => { loadWidgets(); }, [loadWidgets]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, draft, ask]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || thinking) return;
    const next = [...messages, { role: 'user', content }];
    setMessages(next); setInput(''); setThinking(true); setAsk(null); setDraft(null);
    try {
      const r = await api.post(`/v1/projects/${projectId}/overview/kpi-builder/chat`, { messages: next });
      if (r.data?.status === 'spec') {
        setDraft(r.data);
        setMessages([...next, { role: 'assistant', content: r.data.explanation || `Here is “${r.data.spec?.title}”.` }]);
      } else {
        setAsk(r.data);
        setMessages([...next, { role: 'assistant', content: r.data?.question || 'Could you clarify?' }]);
      }
    } catch (e) {
      toast.error('Cognara could not build that KPI');
    } finally { setThinking(false); }
  };

  const saveDraft = async () => {
    try {
      await api.post(`/v1/projects/${projectId}/overview/kpi-builder/widgets`, { spec: draft.spec });
      toast.success('KPI added to your Overview');
      setDraft(null); setMessages([]); setOpen(false);
      loadWidgets();
    } catch { toast.error('Could not save the KPI'); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/v1/projects/${projectId}/overview/kpi-builder/widgets/${id}`);
      setWidgets((w) => w.filter((x) => x.widgetId !== id));
    } catch { toast.error('Could not remove that KPI'); }
  };

  const builderBody = (
    <div className="flex-1 flex flex-col min-h-0 gap-3">
          <div className="flex-1 min-h-[140px] overflow-y-auto space-y-2" data-testid="kpi-builder-transcript">
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-1.5">
                {SEEDS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}
                    className="text-[10px] px-2 py-1 rounded-full"
                    style={{ background: T.inner, border: `1px solid ${T.border}`, color: T.text2 }}
                    data-testid="kpi-builder-seed">{s}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-[11px] max-w-[85%] rounded-xl px-3 py-2 ${m.role === 'user' ? 'ml-auto' : ''}`}
                   style={{ background: m.role === 'user' ? `${T.accent}1A` : T.inner,
                            border: `1px solid ${T.border}`, color: T.text2 }}>
                {m.content}
              </div>
            ))}
            {thinking && (
              <div className="flex items-center gap-2 text-[11px]" style={{ color: T.muted }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cognara is building your KPI…
              </div>
            )}
            {ask?.options?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {ask.options.map((o) => (
                  <button key={o} type="button" onClick={() => send(o)}
                    className="text-[10px] px-2 py-1 rounded-full"
                    style={{ background: T.inner, border: `1px solid ${T.border}`, color: T.text2 }}>{o}</button>
                ))}
              </div>
            )}
            {draft && (
              <div className="rounded-xl p-3" style={{ background: T.inner, border: `1px solid ${T.violet}44` }}
                   data-testid="kpi-builder-preview">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-semibold" style={{ color: T.text }}>{draft.spec?.title}</span>
                  <span className="text-[9px] font-mono ml-auto" style={{ color: T.muted }}>
                    {draft.spec?.module} · {draft.spec?.metric} · {draft.spec?.chartType}
                  </span>
                </div>
                <WidgetChart w={draft} T={T} />
                <button type="button" onClick={saveDraft}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold"
                  style={{ background: T.violet, color: '#fff' }} data-testid="kpi-builder-save">
                  <Save className="w-3.5 h-3.5" /> Pin this KPI to Overview
                </button>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder="e.g. show procurement items at risk by discipline as a donut"
              className="flex-1 text-[11px] rounded-lg px-3 py-2 outline-none"
              style={{ background: T.inner, border: `1px solid ${T.border}`, color: T.text }}
              data-testid="kpi-builder-input" />
            <button type="button" onClick={() => send()} disabled={thinking}
              className="p-2 rounded-lg disabled:opacity-50" style={{ background: T.violet }}
              data-testid="kpi-builder-send">
              <Send className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
    </div>
  );

  const widgetBody = (
    <>
        {widgets.length === 0 ? (
          <div className="py-8 text-center text-[11px]" style={{ color: T.muted }} data-testid="kpi-builder-empty">
            No custom KPIs yet — tap “New KPI” and describe what you want to see.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="kpi-builder-widgets">
            {widgets.map((w, i) => (
              <motion.div key={w.widgetId} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`kpi-widget-${w.widgetId}`}>
                {editingId === w.widgetId ? (
                  <KpiWidgetEditor projectId={projectId} widget={w} T={T}
                    onCancel={() => setEditingId(null)}
                    onSaved={() => { setEditingId(null); loadWidgets(); }} />
                ) : (
                  <div className="rounded-xl p-3 cursor-pointer transition-colors"
                       style={{ background: T.inner, border: `1px solid ${T.border}` }}
                       onClick={() => setEditingId(w.widgetId)}
                       title="Click to edit this KPI"
                       data-testid={`kpi-widget-open-${w.widgetId}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-semibold truncate" style={{ color: T.text }}>{w.spec?.title}</span>
                      <Pencil className="w-3 h-3 shrink-0 opacity-60" style={{ color: T.violet }} />
                      <button type="button"
                              onClick={(e) => { e.stopPropagation(); remove(w.widgetId); }}
                              className="ml-auto p-1 rounded hover:opacity-70"
                              data-testid={`kpi-widget-delete-${w.widgetId}`}>
                        <Trash2 className="w-3 h-3" style={{ color: T.muted }} />
                      </button>
                    </div>
                    {(w.spec?.filterGroup || w.spec?.filterOwner) && (
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {[w.spec.filterGroup, w.spec.filterOwner].filter(Boolean).map((chip) => (
                          <span key={chip} className="text-[9px] px-1.5 py-0.5 rounded-full"
                                style={{ background: `${T.accent}1A`, color: T.accent }}>{chip}</span>
                        ))}
                      </div>
                    )}
                    <WidgetChart w={w} T={T} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
    </>
  );

  const [panelOpen, setPanelOpen] = useState(false);

  if (asPanel) {
    /* Portalled to <body>: the dashboard tree has transformed (framer-motion)
       ancestors, which would otherwise become the containing block for
       position:fixed and let the panel run past the bottom of the viewport. */
    return createPortal(
      <>
        {/* Collapsed rail — always reachable on the right edge */}
        <button type="button" onClick={() => setPanelOpen(true)}
          className="fixed right-0 top-1/3 z-[60] items-center gap-1.5 px-2 py-3 rounded-l-xl shadow-lg transition-transform hover:-translate-x-0.5"
          style={{ background: T.violet, color: '#fff',
                   writingMode: 'vertical-rl', display: panelOpen ? 'none' : 'flex' }}
          data-testid="kpi-builder-rail">
          <Sparkles className="w-3.5 h-3.5" style={{ transform: 'rotate(180deg)' }} />
          <span className="text-[11px] font-semibold tracking-wide">Build a KPI with Cognara</span>
        </button>

        <aside className="fixed right-0 top-0 z-[70] flex flex-col shadow-2xl transition-transform duration-300"
          style={{ width: 'min(460px, 94vw)', height: '100dvh', maxHeight: '100dvh',
                   background: T.card, borderLeft: `1px solid ${T.border}`,
                   transform: panelOpen ? 'translateX(0)' : 'translateX(100%)',
                   pointerEvents: panelOpen ? 'auto' : 'none' }}
          data-testid="project-kpi-builder">
          <div className="px-4 py-3 flex items-center gap-2 shrink-0"
               style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
            <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${T.violet}1F` }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: T.violet }} />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold leading-tight" style={{ color: T.text }}>Build a KPI with Cognara</h3>
              <p className="text-[10px]" style={{ color: T.muted }}>Describe the metric — Cognara pins it here</p>
            </div>
            <button type="button" onClick={() => setPanelOpen(false)} className="ml-auto p-1 rounded hover:opacity-70"
                    data-testid="kpi-builder-collapse">
              <X className="w-4 h-4" style={{ color: T.muted }} />
            </button>
          </div>

          <div className="flex-1 min-h-0 p-4 flex flex-col">{builderBody}</div>

          {widgets.length > 0 && (
            <div className="p-4 overflow-y-auto shrink-0"
                 style={{ maxHeight: editingId ? '72dvh' : '38dvh', borderTop: `1px solid ${T.border}` }}>{widgetBody}</div>
          )}
        </aside>
      </>,
      document.body
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="project-kpi-builder"
         style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <div className="px-5 pt-4 pb-3 flex items-center gap-2"
           style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
        <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${T.violet}1F` }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: T.violet }} />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-tight" style={{ color: T.text }}>Build a KPI with Cognara</h3>
          <p className="text-[10px]" style={{ color: T.muted }}>
            Describe the metric you want — Cognara builds and pins it to this Overview
          </p>
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
          style={{ background: open ? T.inner : T.violet, color: open ? T.text2 : '#fff',
                   border: `1px solid ${open ? T.border : T.violet}` }}
          data-testid="kpi-builder-toggle">
          <Plus className="w-3.5 h-3.5" /> {open ? 'Close builder' : 'New KPI'}
        </button>
      </div>

      {open && (
        <div className="p-4 space-y-3" style={{ borderBottom: `1px solid ${T.border}` }}>
          {builderBody}
        </div>
      )}

      <div className="p-4">{widgetBody}</div>
    </div>
  );
};

export default ProjectKpiBuilder;
